import React, { useMemo } from 'react';
import { Container, Row, Col, Card, CardBody, Table, Button } from 'reactstrap';
// Use styling-capable XLSX with graceful fallback
let XLSX; try { XLSX = require('xlsx-js-style'); } catch (e) { XLSX = require('xlsx'); }

// Removed SAMPLE_ROWS: component now expects real data via dailyData prop.

// Helper function to get days in a specific month
const getDaysInMonth = (month, year) => {
    // Handle both number and string inputs
    const monthNum = typeof month === 'string' ? parseInt(month, 10) : month;
    const yearNum = year || new Date().getFullYear();
    return new Date(yearNum, monthNum, 0).getDate();
};

// Sticky column configuration (first 4 columns)
const STICKY_CONFIG = [
    { key: 'srno', left: 0, width: 40 },
    { key: 'oil_name', left: 40, width: 140 },
    { key: 'machine_name', left: 180, width: 110 },
    { key: 'machine_number', left: 290, width: 90 },
    { key: 'conc_limit', left: 380, width: 140 },
    { key: 'rec_ph', left: 520, width: 120 }
];
// Total width not needed for export logic in this component.

const stickyStyleFor = (key, isHeader = false) => {
    const cfg = STICKY_CONFIG.find(c => c.key === key);
    if (!cfg) return {};
    return {
        position: 'sticky',
        left: cfg.left,
        minWidth: cfg.width,
        maxWidth: cfg.width,
        width: cfg.width,
        background: isHeader ? '#f8f9fa' : '#fff',
        zIndex: isHeader ? 15 : 10,
        whiteSpace: 'nowrap',
        boxShadow: 'inset -1px 0px 0px #e9ecef, inset 0px -1px 0px #e9ecef'
    };
};
// (No spanning sticky cell needed in this component; kept minimal)

const ReportPhConc = ({ onBack, month = new Date().getMonth() + 1, year = new Date().getFullYear(), dailyData = [], companyName = '', ri = false }) => {
    // Calculate days in the selected month
    const DAYS_IN_MONTH = useMemo(() => {
        const monthNum = typeof month === 'string' ? parseInt(month, 10) : month;
        const yearNum = typeof year === 'string' ? parseInt(year, 10) : year;
        return getDaysInMonth(monthNum, yearNum);
    }, [month, year]);

    const DAY_INDICES = useMemo(() => {
        return Array.from({ length: DAYS_IN_MONTH }, (_, i) => i + 1);
    }, [DAYS_IN_MONTH]);

    // Transform backend payload to internal row format if real data exists
    // Backend sample shape: [{ machine_id, machine_number, machine_name, oil_name, daily: { '1': { pH, conc_B, water_added, oil_added, conc_A } ... } }]
    const rows = useMemo(() => {
        const list = Array.isArray(dailyData) ? dailyData : Array.isArray(dailyData?.data) ? dailyData.data : Array.isArray(dailyData?.payload) ? dailyData.payload : [];
        if (!list.length) return [];
        return list.map((item, idx) => {
            const dailyTransformed = {};
            Object.keys(item.daily || {}).forEach(k => {
                const d = item.daily[k];
                dailyTransformed[parseInt(k, 10)] = {
                    pH: d?.pH ?? d?.ph ?? '',
                    conc_B: d?.conc_B ?? d?.concentration_before ?? '',
                    water_added: d?.water_added ?? '',
                    oil_added: d?.oil_added ?? '',
                    conc_A: d?.conc_A ?? d?.concentration_after ?? ''
                };
            });
            return {
                srno: idx + 1,
                oil_name: item.oil_name || '-',
                machine_name: item.machine_name || '-',
                machine_number: item.machine_number || '-',
                conc_limit: `${item.concentration_lower_limit ?? '-'} - ${item.concentration_upper_limit ?? '-'}`,
                rec_ph: item.recommended_pH || '-',
                daily: dailyTransformed
            };
        });
    }, [dailyData]);

    const displayMonth = useMemo(() => {
        const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
        // Handle both number and string inputs
        const monthNum = typeof month === 'string' ? parseInt(month, 10) : month;
        if (typeof monthNum === 'number' && monthNum >= 1 && monthNum <= 12) {
            return monthNames[monthNum - 1];
        }
        // If month is already a string (month name), return it
        if (typeof month === 'string' && monthNames.includes(month)) {
            return month;
        }
        // Fallback to current month name if invalid
        return monthNames[new Date().getMonth()];
    }, [month]);

    const renderHeaders = () => (
        <thead className="thead-light bg-gray-50 border-t-2 border-gray-400">
            {/* Row 1: Base columns + grouped Date heading */}
            <tr>
                <th rowSpan={3} style={stickyStyleFor('srno', true)} className="text-left text-xs font-semibold align-middle py-2 px-2">SL</th>
                <th rowSpan={3} style={stickyStyleFor('oil_name', true)} className="text-left text-xs font-semibold align-middle py-2 px-2">Oil<br />Name</th>
                <th rowSpan={3} style={stickyStyleFor('machine_name', true)} className="text-left text-xs font-semibold align-middle py-2 px-2">Machine<br />Name</th>
                <th rowSpan={3} style={stickyStyleFor('machine_number', true)} className="text-left text-xs font-semibold align-middle py-2 px-2">Machine<br />Number</th>
                <th rowSpan={3} style={stickyStyleFor('conc_limit', true)} className="text-left text-xs font-semibold align-middle py-2 px-2">Concentration<br />Limit<br />(Lower &amp; Upper)</th>
                <th rowSpan={3} style={stickyStyleFor('rec_ph', true)} className="text-left text-xs font-semibold align-middle py-2 px-2">Recommended<br />PH</th>
                <th colSpan={DAY_INDICES.length * 5} className="text-center text-xs font-semibold">Date &gt; ({displayMonth})</th>
            </tr>
            {/* Row 2: Day numbers (each spans five sub-columns) */}
            <tr>
                {DAY_INDICES.map(day => (
                    <th key={`dtop-${day}`} colSpan={5} className="text-center text-xs font-semibold" style={{ minWidth: 150 }}>{day}</th>
                ))}
            </tr>
            {/* Row 3: pH / concentration sub headers */}
            <tr>
                {DAY_INDICES.map(day => (
                    <React.Fragment key={`dsub-${day}`}>
                        <th className="text-center text-xs " style={{ minWidth: 30 }}>pH</th>
                        <th className="text-center text-xs " style={{ minWidth: 40 }}>Conc. Before</th>
                        <th className="text-center text-xs " style={{ minWidth: 40 }}>Water Added</th>
                        <th className="text-center text-xs " style={{ minWidth: 40 }}>Oil Added</th>
                        <th className="text-center text-xs " style={{ minWidth: 40 }}>Conc. After</th>
                    </React.Fragment>
                ))}
            </tr>
        </thead>
    );

    const renderRow = (row) => (
        <tr key={row.srno} className="table-row-hover">
            <td style={stickyStyleFor('srno')} className="text-left text-xs align-middle py-2 px-2">{row.srno}</td>
            <td style={stickyStyleFor('oil_name')} className="text-left text-xs align-middle py-2 px-2">{row.oil_name}</td>
            <td style={stickyStyleFor('machine_name')} className="text-left text-xs align-middle py-2 px-2">{row.machine_name}</td>
            <td style={stickyStyleFor('machine_number')} className="text-left text-xs align-middle py-2 px-2">{row.machine_number}</td>
            <td style={stickyStyleFor('conc_limit')} className="text-left text-xs align-middle py-2 px-2">{row.conc_limit}</td>
            <td style={stickyStyleFor('rec_ph')} className="text-left text-xs align-middle py-2 px-2">{row.rec_ph}</td>
            {DAY_INDICES.map(day => {
                const entry = row.daily[day];
                return (
                    <React.Fragment key={`${row.srno}-${day}`}>
                        <td className="text-center text-xxs align-middle py-2 px-1">{entry ? entry.pH : ''}</td>
                        <td className="text-center text-xxs align-middle py-2 px-1">{entry ? entry.conc_B : ''}</td>
                        <td className="text-center text-xxs align-middle py-2 px-1">{entry ? entry.water_added : ''}</td>
                        <td className="text-center text-xxs align-middle py-2 px-1">{entry ? entry.oil_added : ''}</td>
                        <td className="text-center text-xxs align-middle py-2 px-1">{entry ? entry.conc_A : ''}</td>
                    </React.Fragment>
                );
            })}
        </tr>
    );

    // Export to Excel replicating multi-row header layout (Report title + 3-tier table header)
    const handleExport = () => {
        try {
            const totalDayCols = DAY_INDICES.length * 5; // five per day
            const totalCols = 6 + totalDayCols; // base cols + day subcols

            const sheetData = [];
            // Row1: Subtitle with month
            sheetData.push([`Monthly daywise Coolant Data Report for pH and Concentration (${displayMonth} - ${year})`]);
            // Row2: Company row
            sheetData.push([`Company: ${companyName || '-'}`]);
            // Row3: First header tier (Srno..Machine Number + Date > (...))
            const topHeader = ['SL', 'Oil Name', 'Machine Name', 'Machine Number', 'Concentration Limit\n(Lower & Upper)', 'Recommended\nPH'];
            // Fill remainder placeholders for row3 so aoa_to_sheet has consistent length
            while (topHeader.length < totalCols) topHeader.push('');
            sheetData.push(topHeader);
            // Row4: Day numbers (each spanning 5 columns) - keep first 6 columns empty for proper alignment
            const dayNumberRow = ['', '', '', '', '', ''];
            DAY_INDICES.forEach(d => {
                dayNumberRow.push(d.toString());
                dayNumberRow.push('', '', '', ''); // remaining cells within merged group
            });
            sheetData.push(dayNumberRow);
            // Row5: Sub headers pH / Conc. Before / Water Added / Oil Added / Conc. After - keep first 6 columns empty for proper alignment
            const subHeaderRow = ['', '', '', '', '', ''];
            DAY_INDICES.forEach(() => {
                subHeaderRow.push('pH');
                subHeaderRow.push('Conc. Before');
                subHeaderRow.push('Water Added');
                subHeaderRow.push('Oil Added');
                subHeaderRow.push('Conc. After');
            });
            sheetData.push(subHeaderRow);
            // Data rows start from Row 6 (after title, company, and 3 header rows)
            rows.forEach(r => {
                const rowArr = [r.srno, r.oil_name, r.machine_name, r.machine_number, r.conc_limit, r.rec_ph];
                DAY_INDICES.forEach(d => {
                    const val = r.daily[d];
                    rowArr.push(val?.pH ?? '');
                    rowArr.push(val?.conc_B ?? '');
                    rowArr.push(val?.water_added ?? '');
                    rowArr.push(val?.oil_added ?? '');
                    rowArr.push(val?.conc_A ?? '');
                });
                sheetData.push(rowArr);
            });

            const ws = XLSX.utils.aoa_to_sheet(sheetData);
            // Build merges
            const merges = [];
            const lastColIndex = totalCols - 1;
            // Title and company full-width merges
            merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: lastColIndex } }); // Title row
            merges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: lastColIndex } }); // Company row

            // Vertical merges for first 6 columns over rows 2-4 (header rows only, not including title/company)
            for (let c = 0; c < 6; c++) {
                merges.push({ s: { r: 2, c }, e: { r: 4, c } });
            }

            // Merge Date > header across all day columns (row 2, columns 6 to end)
            merges.push({ s: { r: 2, c: 6 }, e: { r: 2, c: lastColIndex } });

            // Merge each day number across its five sub columns (row 3)
            DAY_INDICES.forEach((_, idx) => {
                const startCol = 6 + idx * 5;
                merges.push({ s: { r: 3, c: startCol }, e: { r: 3, c: startCol + 4 } });
            });
            ws['!merges'] = merges;

            // Column widths (approximate to screenshot)
            const cols = [
                { wch: 6 }, // Srno
                { wch: 18 }, // Oil Name
                { wch: 18 }, // Machine Name
                { wch: 18 }, // Machine Number
                { wch: 22 }, // Conc Limit
                { wch: 16 }, // Rec pH
            ];
            DAY_INDICES.forEach(() => {
                cols.push({ wch: 5 }); // pH
                cols.push({ wch: 12 }); // Conc Before
                cols.push({ wch: 10 }); // Water Added
                cols.push({ wch: 10 }); // Oil Added
                cols.push({ wch: 12 }); // Conc After
            });
            ws['!cols'] = cols;

            // Basic styling
            const applyStyle = (cellRef, style) => { if (ws[cellRef]) ws[cellRef].s = style; };
            applyStyle('A1', { font: { bold: true, sz: 12 }, alignment: { horizontal: 'left' } }); // Title
            applyStyle('A2', { font: { bold: false, sz: 11 }, alignment: { horizontal: 'left' } }); // Company

            // Style the main column headers (row 3: S.no, Oil Name, Machine Name, Machine Number)
            applyStyle('A3', { font: { bold: true, sz: 10 }, alignment: { horizontal: 'center', vertical: 'center' } });
            applyStyle('B3', { font: { bold: true, sz: 10 }, alignment: { horizontal: 'center', vertical: 'center' } });
            applyStyle('C3', { font: { bold: true, sz: 10 }, alignment: { horizontal: 'center', vertical: 'center' } });
            applyStyle('D3', { font: { bold: true, sz: 10 }, alignment: { horizontal: 'center', vertical: 'center' } });
            applyStyle('E3', { font: { bold: true, sz: 10 }, alignment: { horizontal: 'center', vertical: 'center', wrapText: true } });
            applyStyle('F3', { font: { bold: true, sz: 10 }, alignment: { horizontal: 'center', vertical: 'center', wrapText: true } });
            applyStyle('G3', { font: { bold: true, sz: 10 }, alignment: { horizontal: 'center' } }); // Date header

            // Style header rows (rows 3-5) for all day columns
            const headerRows = [3, 4, 5];
            headerRows.forEach(rIdx => {
                for (let c = 6; c < totalCols; c++) { // Start from column 6 (day columns)
                    const ref = XLSX.utils.encode_cell({ r: rIdx - 1, c });
                    if (ws[ref]) {
                        ws[ref].s = ws[ref].s || {};
                        ws[ref].s.font = { ...(ws[ref].s.font || {}), bold: true, sz: 9 };
                        ws[ref].s.alignment = { horizontal: 'center', vertical: 'center', wrapText: true };
                    }
                }
            });

            // Create workbook & save
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Monthly pH');
            const today = new Date().toISOString().split('T')[0];
            const filename = `Monthly_Coolant_pH_Conc_Report_${displayMonth}_${today}.xlsx`.replace(/\s+/g, '_');
            XLSX.writeFile(wb, filename);
        } catch (err) {
            console.error('Export failed', err);
            alert('Failed to export Excel');
        }
    };


    return (
        <Container fluid className="mt-4">
            <Row>
                <Col xl="12">
                    <Card className="shadow border-0">
                        <CardBody className="bg-gray-50">
                            <Row className="mb-3">
                                <Col lg="6">
                                    <div className="text-sm text-gray-500">
                                        <Button color="link" onClick={onBack} className="p-0 text-xs no-underline">
                                            <i className="fa fa-arrow-left mr-2" />Back
                                        </Button>
                                        <span style={{ fontWeight: 600, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center' }}>
                                            <i className="fa fa-flask ml-2 mr-1" />Monthly Coolant pH & Concentration Report{companyName ? ` - ${companyName}` : ''} - ({displayMonth} - {year}) - {rows.length} records
                                        </span>
                                    </div>
                                </Col>
                                <Col lg="6" className="text-right">
                                    <div
                                        onClick={handleExport}
                                        disabled={rows.length === 0}
                                        style={{
                                            padding: '2px 4px',
                                            fontSize: '13px',
                                            fontWeight: '500',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            cursor: 'pointer',
                                            color: '#418efc',
                                            borderBottom: '1.5px solid #418efc',
                                        }}
                                    >
                                        <i className="fa-regular fa-circle-down" style={{ fontSize: '13px' }}></i>
                                        Download Excel
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col xs="12">
                                    <div className="overflow-x-auto">
                                        <Table className="table-hover table-flush table-bordered text-sm mb-0 bg-white" responsive>
                                            {renderHeaders()}
                                            <tbody>
                                                {rows.map(renderRow)}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default ReportPhConc;